/**
 * Google Analytics GA4 Adapter
 * 
 * Adapts event data into Google Analytics GA4 format.
 * 
 * Documentation:
 * - https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 * 
 * @since 1.51.0
 */

(function (pmw, undefined) {

	class GoogleAnalyticsAdapter {
		
		/**
		 * Adapt event data to Google Analytics GA4 format
		 * 
		 * @param {object} eventData - Event-specific data (product, order, etc.)
		 * @param {string} eventName - Standardized event name
		 * @param {object} context - Event context (user, page, etc.)
		 * @returns {object|null} Adapted data for GA4 or null if pixel not loaded
		 */
		adapt(eventData, eventName, context) {
			
			// Check if GA4 is loaded
			if (!pmwDataLayer?.pixels?.google?.analytics?.ga4?.measurement_id) {
				return null;
			}
			
			const eventMap = {
				'view_item_list': () => this.adaptViewItemList(eventData),
				'select_item': () => this.adaptSelectItem(eventData),
				'add_to_cart': () => this.adaptAddToCart(eventData),
				'view_item': () => this.adaptViewItem(eventData),
				'add_to_wishlist': () => this.adaptAddToWishlist(eventData),
				'remove_from_cart': () => this.adaptRemoveFromCart(eventData),
				'begin_checkout': () => this.adaptBeginCheckout(eventData),
				'add_shipping_info': () => this.adaptAddShippingInfo(eventData),
				'add_payment_info': () => this.adaptAddPaymentInfo(eventData),
				'view_cart': () => this.adaptViewCart(eventData),
				'search': () => this.adaptSearch(eventData),
				'purchase': () => this.adaptPurchase(eventData),
			};
			
			if (!eventMap[eventName]) {
				return null;
			}
			
			return eventMap[eventName]();
		}
		
		/**
		 * Adapt view_item_list event
		 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#view_item_list
		 */
		adaptViewItemList(eventData) {
			if (!eventData?.product) return null;
			
			return {
				event_name: 'view_item_list',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
					items: [pmw.ga4GetFullProductItemData(eventData.product)],
					item_list_name: pmwDataLayer.shop.list_name,
					item_list_id: pmwDataLayer.shop.list_id,
				}
			};
		}
		
		/**
		 * Adapt select_item event
		 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#select_item
		 */
		adaptSelectItem(eventData) {
			if (!eventData?.product) return null;
			
			return {
				event_name: 'select_item',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
					items: [pmw.ga4GetFullProductItemData(eventData.product)],
				}
			};
		}
		
		/**
		 * Adapt add_to_cart event
		 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#add_to_cart
		 */
		adaptAddToCart(eventData) {
			if (!eventData?.product) return null;
			
			const product = eventData.product;
			
			return {
				event_name: 'add_to_cart',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
					currency: pmwDataLayer?.shop?.currency,
					value: product.price * product.quantity,
					items: [pmw.ga4GetFullProductItemData(product)],
				}
			};
		}
		
		/**
		 * Adapt view_item event
		 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#view_item
		 */
		adaptViewItem(eventData) {
			const data = {
				event_name: 'view_item',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
				}
			};
			
			if (eventData?.product) {
				data.event_data.currency = pmwDataLayer?.shop?.currency;
				data.event_data.items = [pmw.ga4GetFullProductItemData(eventData.product)];
			}
			
			return data;
		}
		
		/**
		 * Adapt add_to_wishlist event
		 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#add_to_wishlist
		 */
		adaptAddToWishlist(eventData) {
			if (!eventData?.product) return null;
			
			const product = eventData.product;
			
			return {
				event_name: 'add_to_wishlist',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
					currency: pmwDataLayer?.shop?.currency,
					value: product.price * product.quantity,
					items: [pmw.ga4GetFullProductItemData(product)],
				}
			};
		}
		
		/**
		 * Adapt remove_from_cart event
		 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#remove_from_cart
		 */
		adaptRemoveFromCart(eventData) {
			if (!eventData?.product) return null;
			
			const product = eventData.product;
			
			return {
				event_name: 'remove_from_cart',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
					currency: pmwDataLayer?.shop?.currency,
					value: product.price * product.quantity,
					items: [pmw.ga4GetFullProductItemData(product)],
				}
			};
		}
		
		/**
		 * Adapt begin_checkout event
		 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#begin_checkout
		 */
		adaptBeginCheckout(eventData) {
			const data = {
				event_name: 'begin_checkout',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
				}
			};

			// Only attach commerce data when the tracked cart has contents. An empty
			// tracked cart at checkout time usually means the cart was populated
			// outside of client-side tracking, so value 0 and an empty items array
			// would report false zeros. The event itself still fires, because the
			// visitor did start the checkout.
			if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {
				data.event_data.currency = pmwDataLayer?.shop?.currency;
				data.event_data.value = pmw.getCartValue();
				data.event_data.items = pmw.getCartItemsGa4();
			}

			return data;
		}
		
		/**
		 * Adapt add_shipping_info event
		 */
		adaptAddShippingInfo(eventData) {
			const data = {
				event_name: 'add_shipping_info',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
				}
			};

			// Only attach commerce data when the tracked cart has contents,
			// mirroring adaptBeginCheckout.
			if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {
				data.event_data.currency = pmwDataLayer?.shop?.currency;
				data.event_data.value = pmw.getCartValue();
				data.event_data.items = pmw.getCartItemsGa4();
			}

			if (eventData?.shippingTier?.text) {
				data.event_data.shipping_tier = eventData.shippingTier.text;
			}

			return data;
		}
		
		/**
		 * Adapt add_payment_info event
		 */
		adaptAddPaymentInfo(eventData) {
			const data = {
				event_name: 'add_payment_info',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
				}
			};

			// Only attach commerce data when the tracked cart has contents,
			// mirroring adaptBeginCheckout.
			if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {
				data.event_data.currency = pmwDataLayer?.shop?.currency;
				data.event_data.value = pmw.getCartValue();
				data.event_data.items = pmw.getCartItemsGa4();
			}

			if (eventData?.paymentType?.text) {
				data.event_data.payment_type = eventData.paymentType.text;
			}

			return data;
		}
		
		/**
		 * Adapt view_cart event
		 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#view_cart
		 */
		adaptViewCart(eventData) {
			if (pmw.isEmptyObject(pmwDataLayer.cart)) return null;
			
			let products = [];
			let cartValue = 0;
			
			Object.values(pmwDataLayer.cart).forEach(product => {
				products.push(pmw.ga4GetFullProductItemData(product));
				cartValue = cartValue + product.quantity * product.price;
			});
			
			return {
				event_name: 'view_cart',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
					currency: pmwDataLayer?.shop?.currency,
					value: cartValue.toFixed(2),
					items: products,
				}
			};
		}
		
		/**
		 * Adapt search event
		 */
		adaptSearch(eventData) {
			let products = [];
			
			Object.values(pmwDataLayer.products).forEach(product => {
				products.push(pmw.ga4GetFullProductItemData(product));
			});
			
			return {
				event_name: 'view_search_results',
				event_data: {
					send_to: pmwDataLayer.pixels.google.analytics.ga4.measurement_id,
					search_term: pmw.getSearchTermFromUrl(),
					items: products,
				}
			};
		}
		
		/**
		 * Adapt purchase event
		 */
		adaptPurchase(eventData) {
			// Skip if Measurement Protocol is active (server-side tracking)
			if (pmwDataLayer?.pixels?.google?.analytics?.ga4?.mp_active) {
				return null;
			}
			
			const data = {
				event_name: 'purchase',
				event_data: {
					send_to: [pmwDataLayer.pixels.google.analytics.ga4.measurement_id],
					transaction_id: pmwDataLayer.order.number,
					affiliation: pmwDataLayer.order.affiliation,
					currency: pmwDataLayer.order.currency,
					value: pmwDataLayer.order.value.total,
					discount: pmwDataLayer.order.discount,
					tax: pmwDataLayer.order.tax,
					shipping: pmwDataLayer.order.shipping,
					coupon: pmwDataLayer.order.coupon,
					items: pmw.getGA4OrderItems(),
					payment_type: pmwDataLayer.order.payment_type,
					payment_type_title: pmwDataLayer.order.payment_type_title,
				}
			};
			
			// Add custom parameters if present
			if (pmwDataLayer?.order?.custom_parameters) {
				Object.keys(pmwDataLayer.order.custom_parameters).forEach(key => {
					data.event_data[key] = pmwDataLayer.order.custom_parameters[key];
				});
			}
			
			return data;
		}
	}
	
	// Register the adapter
	pmw.registerAdapter('google_analytics', new GoogleAnalyticsAdapter(), {
		isActive: () => pmwDataLayer?.pixels?.google?.analytics?.ga4?.measurement_id ? true : false
	});
	
}(window.pmw = window.pmw || {}));
